import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { delay, of } from 'rxjs';
import { CHECK } from '../../../../DummyFiles/checks';
import { DOCUMENT, DOCUMENTS_FOR_CHECK } from '../../../../DummyFiles/documents';
import { COMMENT } from '../../../../DummyFiles/comments';
import { CommentDTO } from '../../models/comment-model';
import { PROJECT } from '../../../../DummyFiles/projects';
import { USERS } from '../../../../DummyFiles/users';
import { API_ENDPOINTS } from './api-endpoints';

/**
 * Mock-Store für Dokument-Kommentare (In-Memory, nur im Mock-Betrieb).
 * Vorbelegt mit je ein paar Kommentaren für die Test-Dokumente.
 */
const docCommentStore: Record<string, CommentDTO[]> = {
  'bbbbbbbb-0000-0000-0000-000000000001': COMMENT.slice(0, 2),
  'bbbbbbbb-0000-0000-0000-000000000002': COMMENT.slice(2, 4),
};

/** Zwischenspeicher für frisch erstellte Kommentare (POST /check/stage/comment) */
const tempCreatedComments: Record<string, CommentDTO> = {};

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.useMocks) {
    // Relative /api/-Pfade mit der Backend-Base-URL versehen
    if (environment.apiBaseUrl && req.url.startsWith('/api/')) {
      const apiReq = req.clone({ url: `${environment.apiBaseUrl}${req.url}` });
      return next(apiReq);
    }
    return next(req);
  }

  console.log('[API Simulation] Request:', req.url, req.method);

  // Hilfsfunktion: User aus USERS-Array per OID suchen
  const userFor = (oid?: string | null) =>
    oid ? { oid, displayName: USERS.find(u => u.oid === oid)?.displayName ?? oid } : null;


  // ── GET /api/check/{guid}/stage/{stageId}/comment ──
  if (req.url.match(/\/api\/check\/[\w-]+\/stage\/[\w-]+\/comment$/) && req.method === 'GET') {
    return of(new HttpResponse({ status: 200, body: COMMENT })).pipe(delay(300));
  }

  // ── POST /api/check/{guid}/stage/{stageId}/comment → 201 ──
  if (req.url.match(/\/api\/check\/[\w-]+\/stage\/[\w-]+\/comment$/) && req.method === 'POST') {
    const body = req.body as any;
    const newComment: CommentDTO = {
      guid: crypto.randomUUID(),
      author: { oid: USERS[0].oid, displayName: USERS[0].displayName },
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
      text: body?.text ?? '',
      isDone: false,
      severity: body?.severity ?? 'WARNING',
    };
    // Im Zwischenspeicher ablegen, damit POST /document/.../comment/{id} ihn findet
    tempCreatedComments[newComment.guid] = newComment;
    return of(new HttpResponse({ status: 201, body: newComment })).pipe(delay(300));
  }

  // ── GET /api/check/{guid}/comment ──
  if (req.url.match(/\/api\/check\/[\w-]+\/comment$/) && req.method === 'GET') {
    return of(new HttpResponse({ status: 200, body: COMMENT })).pipe(delay(300));
  }

  // ── GET /api/check/{guid}/stage ──
  if (req.url.match(/\/api\/check\/[\w-]+\/stage$/) && req.method === 'GET') {
    const guidMatch = req.url.match(/\/api\/check\/([\w-]+)\/stage/);
    const guid = guidMatch?.[1] ?? '';
    const check = CHECK.find(c => c.guid === guid);
    const stages = [];
    if (check?.checker11) {
      stages.push({
        id: 1, phase: 'FIRST', status: 'CREATED', createdAt: check.createdAt,
        assignee: userFor(check.checker11),
        dueAt: check.dueDate1 ?? new Date().toISOString(),
      });
    }
    if (check?.checker12) {
      stages.push({
        id: 2, phase: 'SECOND', status: 'CREATED', createdAt: check.createdAt,
        assignee: userFor(check.checker12),
        dueAt: check.dueDate1 ?? new Date().toISOString(),
      });
    }
    if (check?.checker2) {
      stages.push({
        id: 3, phase: 'REVIEW', status: 'CREATED', createdAt: check.createdAt,
        assignee: userFor(check.checker2),
        dueAt: check.dueDate2 ?? new Date().toISOString(),
      });
    }
    return of(new HttpResponse({ status: 200, body: stages })).pipe(delay(300));
  }

  // ── PUT /api/check/{guid}/status → 202 (CheckStatusUpdateRequest) ──
  if (req.url.match(/\/api\/check\/[\w-]+\/status$/) && req.method === 'PUT') {
    const guidMatch = req.url.match(/\/api\/check\/([\w-]+)\/status/);
    const guid = guidMatch?.[1] ?? '';
    const body = req.body as any;
    const stages = [];
    if (body?.checkerOid1) {
      stages.push({
        id: 1, phase: 'FIRST', status: 'CREATED', createdAt: new Date().toISOString(),
        assignee: userFor(body.checkerOid1),
        dueAt: body.dueDate1,
      });
    }
    if (body?.checkerOid12) {
      stages.push({
        id: 2, phase: 'SECOND', status: 'CREATED', createdAt: new Date().toISOString(),
        assignee: userFor(body.checkerOid12),
        dueAt: body.dueDate12,
      });
    }
    if (body?.checkerOid2) {
      stages.push({
        id: 3, phase: 'REVIEW', status: 'CREATED', createdAt: new Date().toISOString(),
        assignee: userFor(body.checkerOid2),
        dueAt: body.dueDate2,
      });
    }
    return of(new HttpResponse({ status: 202, body: stages })).pipe(delay(300));
  }

  // ── POST /api/check/{guid}/document/{docId} → 201 ──
  if (req.url.match(/\/api\/check\/[\w-]+\/document\/[\w-]+$/) && req.method === 'POST') {
    const match = req.url.match(/\/api\/check\/([\w-]+)\/document\/([\w-]+)/);
    const checkId = match?.[1] ?? '';
    const docId   = match?.[2] ?? '';
    const doc = DOCUMENT.find(d => d.guid === docId);
    const body = doc ? [{ ...doc, checks: [] }] : [];
    return of(new HttpResponse({ status: 201, body })).pipe(delay(300));
  }

  // ── GET /api/check/{guid}/document ──
  if (req.url.match(/\/api\/check\/[\w-]+\/document$/) && req.method === 'GET') {
    const guidMatch = req.url.match(/\/api\/check\/([\w-]+)\/document/);
    const guid = guidMatch?.[1] ?? '';
    const docs = DOCUMENTS_FOR_CHECK[guid] ?? [];
    return of(new HttpResponse({ status: 200, body: docs })).pipe(delay(300));
  }

  // ── GET /api/check/{guid} ──
  if (req.url.match(/\/api\/check\/[\w-]+$/) && req.method === 'GET') {
    const guidMatch = req.url.match(/\/api\/check\/([\w-]+)$/);
    const guid = guidMatch?.[1] ?? '';
    const check = CHECK.find(c => c.guid === guid) ?? null;
    return of(new HttpResponse({ status: check ? 200 : 404, body: check })).pipe(delay(300));
  }

  // ── POST /api/check → 201 ──
  if (req.url === '/api/check' && req.method === 'POST') {
    const body = req.body as any;
    const newCheck = {
      guid: crypto.randomUUID(),
      description: body?.description ?? '',
      createdAt: new Date().toISOString(),
      doneAt: null,
    };
    return of(new HttpResponse({ status: 201, body: newCheck })).pipe(delay(300));
  }

  // ── POST /api/check → 201 ──
  if (req.url.match(/\/api\/comment\/[\w-]+$/) && req.method === 'PUT') {
    return of(new HttpResponse({ status: 202, body: req.body })).pipe(delay(300));
  }

  // ── DELETE /api/comment/{guid} -> 204 ──
  if (req.url.match(/\/api\/comment\/[\w-]+$/) && req.method === 'DELETE') {
    return of(new HttpResponse({ status: 204, body: req.body })).pipe(delay(300));
  }

  // ── Documents (MOCK-ONLY Legacy) ──
  if (req.url.includes(API_ENDPOINTS.DOCUMENTS) && req.method === 'GET') {
    return of(new HttpResponse({ status: 200, body: DOCUMENT })).pipe(delay(300));
  }

  // ── GET /api/document/{id}/comment → CommentDTO[] ──
  if (req.url.match(/\/api\/document\/[\w-]+\/comment$/) && req.method === 'GET') {
    const match = req.url.match(/\/api\/document\/([\w-]+)\/comment$/);
    const docId = match?.[1] ?? '';
    return of(new HttpResponse({ status: 200, body: docCommentStore[docId] ?? [] })).pipe(delay(300));
  }

  // ── POST /api/document/{id}/comment/{commentId} → 201 ──
  if (req.url.match(/\/api\/document\/[\w-]+\/comment\/[\w-]+$/) && req.method === 'POST') {
    const match = req.url.match(/\/api\/document\/([\w-]+)\/comment\/([\w-]+)$/);
    const docId     = match?.[1] ?? '';
    const commentId = match?.[2] ?? '';
    // Kommentar aus Zwischenspeicher oder statischer Liste suchen
    const comment = tempCreatedComments[commentId] ?? COMMENT.find(c => c.guid === commentId);
    if (comment) {
      docCommentStore[docId] = [...(docCommentStore[docId] ?? []), comment];
    }
    return of(new HttpResponse({ status: 201, body: comment ?? null })).pipe(delay(300));
  }

  // ── GET /api/document/{docId}/check → CheckCheckStageAggregate[] ──
  if (req.url.match(/\/api\/document\/[\w-]+\/check$/) && req.method === 'GET') {
    const docIdMatch = req.url.match(/\/api\/document\/([\w-]+)\/check/);
    const docId = docIdMatch?.[1] ?? '';
    const doc = DOCUMENT.find(d => d.guid === docId);
    const body = (doc?.checks ?? []).map(checkDto => {
      const c = CHECK.find(x => x.guid === checkDto.guid);
      const stages = [];
      if (c?.checker11) stages.push({ id: 1, phase: 'FIRST',  status: 'CREATED', createdAt: c.createdAt, assignee: userFor(c.checker11), dueAt: c.dueDate1 ?? null });
      if (c?.checker12) stages.push({ id: 2, phase: 'SECOND', status: 'CREATED', createdAt: c.createdAt, assignee: userFor(c.checker12), dueAt: c.dueDate1 ?? null });
      if (c?.checker2)  stages.push({ id: 3, phase: 'REVIEW', status: 'CREATED', createdAt: c.createdAt, assignee: userFor(c.checker2),  dueAt: c.dueDate2 ?? null });
      return { guid: checkDto.guid, description: checkDto.description, createdAt: checkDto.createdAt, doneAt: null, stages };
    });
    return of(new HttpResponse({ status: 200, body })).pipe(delay(300));
  }

  // ── POST /api/project/{code}/document → 201 ──
  if (req.url.match(/\/api\/project\/[^/]+\/document$/) && req.method === 'POST') {
    const body = req.body as any;
    const newDoc = {
      guid: crypto.randomUUID(),
      uri: body?.uri ?? '',
      displayName: body?.displayName ?? '',
      checks: [],
    };
    DOCUMENT.push(newDoc);
    return of(new HttpResponse({ status: 201, body: newDoc })).pipe(delay(300));
  }

  // ── GET /api/project/{code}/document → DocumentCheckAggregate[] ──
  if (req.url.match(/\/api\/project\/[^/]+\/document$/) && req.method === 'GET') {
    const codeMatch = req.url.match(/\/api\/project\/([^/]+)\/document/);
    const code = codeMatch?.[1] ?? '';
    const projectCheckGuids = new Set(CHECK.filter(c => c.projectCode === code).map(c => c.guid));
    const docs = DOCUMENT
      .filter(d => d.checks?.some(c => projectCheckGuids.has(c.guid)) ?? false)
      .map(d => ({
        guid: d.guid,
        displayName: d.displayName,
        uri: d.uri,
        checks: (d.checks ?? []).map(c => ({ guid: c.guid, createdAt: c.createdAt, description: c.description })),
      }));
    return of(new HttpResponse({ status: 200, body: docs })).pipe(delay(300));
  }

  // ── GET /api/project/{code} ──
  if (req.url.match(/\/api\/project\/[^/]+$/) && req.method === 'GET') {
    const codeMatch = req.url.match(/\/api\/project\/([^/]+)$/);
    const code = codeMatch?.[1] ?? '';
    const project = PROJECT.find(p => p.contractNr === code) ?? null;
    return of(new HttpResponse({ status: project ? 200 : 404, body: project })).pipe(delay(300));
  }

  // ── GET /api/project/{code}/member ──
  if (req.url.match(/\/api\/project\/[^/]+\/member$/) && req.method === 'GET') {
    const members = PROJECT.map((p, i) => ({ id: i + 1, role: 'PROJECT_MEMBER', project: p }));
    return of(new HttpResponse({ status: 200, body: members })).pipe(delay(300));
  }

  // ── GET /api/user/{oid}/member ──
  if (req.url.match(/\/api\/user\/[^/]+\/member$/) && req.method === 'GET') {
    const members = PROJECT.map((p, i) => ({ id: i + 1, role: 'PROJECT_MEMBER', project: p }));
    return of(new HttpResponse({ status: 200, body: members })).pipe(delay(300));
  }

  // ── POST /api/user → 201 (Touch/Create aktuell eingeloggter User) ──
  if (req.url === API_ENDPOINTS.USER && req.method === 'POST') {
    const user = USERS[0] ?? null;
    return of(new HttpResponse({ status: 201, body: user })).pipe(delay(300));
  }

  // ── GET /api/user/{oid} ──
  if (req.url.match(/\/api\/user\/[\w-]+$/) && req.method === 'GET') {
    const oidMatch = req.url.match(/\/api\/user\/([\w-]+)$/);
    const oid = oidMatch?.[1] ?? '';
    const user = USERS.find(u => u.oid === oid) ?? null;
    return of(new HttpResponse({ status: user ? 200 : 404, body: user })).pipe(delay(300));
  }

  return next(req);
};
